/**
 * Evidence-Informed Performance Analytics & Coaching for Aim Assist Training Lab
 * Grounded in deliberate practice, motor learning, and transparent skill diagnostics
 */

import { REFLEX_TIERS } from './constants.js';

export function getReflexTier(score, accuracy = 100) {
  for (const tier of REFLEX_TIERS) {
    if (score >= tier.minScore && accuracy >= (tier.minAccuracy || 0)) {
      return tier;
    }
  }
  return REFLEX_TIERS[REFLEX_TIERS.length - 1];
}

export function analyzeSessionPerformance(session, isArabic = true) {
  const { mode, accuracy = 0, shots = 0, hits = 0, falseStarts = 0, avgAcquisitionMs = 0, trackingPct = 0 } = session;

  let strongestSkillAr = '';
  let strongestSkillEn = '';
  let weakestSkillAr = '';
  let weakestSkillEn = '';
  let diagnosticTipAr = '';
  let diagnosticTipEn = '';
  let nextDrillId = 'precision';

  // 1. Analyze Accuracy vs Speed
  if (accuracy >= 85) {
    strongestSkillAr = 'انضباط زناد استثنائي ونسبة دقة متقدمة';
    strongestSkillEn = 'Exceptional Trigger Discipline & Pinpoint Accuracy';
  } else if (accuracy >= 70) {
    strongestSkillAr = 'استقرار جيد في التصويب والتمركز';
    strongestSkillEn = 'Solid Aim Placement & Consistency';
  } else {
    weakestSkillAr = 'تسرع في إطلاق النار وهدر طلقات بدون استقرار';
    weakestSkillEn = 'Premature Firing & Crosshair Instability';
  }

  // 2. Mode-Specific Nuances
  switch (mode) {
    case 'precision':
      if (avgAcquisitionMs > 0 && avgAcquisitionMs < 380) {
        strongestSkillAr = 'سرعة فليك واقتناص خاطف للأهداف';
        strongestSkillEn = 'Rapid Snap & Flick Acquisition';
      }
      if (accuracy < 75) {
        weakestSkillAr = 'التسرع في النقر قبل وصول المؤشر لمنتصف الهدف';
        weakestSkillEn = 'Clicking Before Crosshair Stabilizes on Target';
        diagnosticTipAr = 'طبق قاعدة الدقة أولاً: أبطئ سرعة حركة يدك بنسبة 15% حتى تتجاوز دقة إصاباتك 85%، فالذاكرة العضلية تبنى على الدقة وليس التكرار العشوائي.';
        diagnosticTipEn = 'Prioritize precision over speed: Slow down hand movement by 15% until accuracy exceeds 85%. Motor memory solidifies from clean repetitions.';
        nextDrillId = 'precision';
      } else {
        diagnosticTipAr = 'مستوى دقة ممتاز. يمكنك الآن زيادة سرعة الانتقال الخاطف تدريجياً لاختبار حدود سرعة استجابتك العصبية.';
        diagnosticTipEn = 'Outstanding accuracy. Gradually increase flick velocity while maintaining your center-target confirmation.';
        nextDrillId = 'switching';
      }
      break;

    case 'tracking':
      if (trackingPct >= 70) {
        strongestSkillAr = 'تتبع سلس فائق وثبات ممتاز على الأهداف المتحركة';
        strongestSkillEn = 'Smooth Continuous Tracking & Minimal Target Loss';
      } else {
        weakestSkillAr = 'محاولة توقع حركة الهدف بدلاً من القراءة البصرية الفعلية';
        weakestSkillEn = 'Predicting Target Path Instead of Visual Confirmation';
        diagnosticTipAr = 'ركز نظرك على حواف الهدف نفسه ولا تتوقع اتجاهه مسبقاً. أرخِ عضلات معصمك وذراعك لتجنب التشنج الذي يسبب ارتداد المؤشر بعيداً.';
        diagnosticTipEn = 'Fixate your gaze on the target rather than predicting its trajectory. Relax your wrist and arm to prevent muscle micro-tension.';
        nextDrillId = 'tracking';
      }
      break;

    case 'switching':
      if (accuracy >= 80) {
        strongestSkillAr = 'انتقال حركي انسيابي وإدارة ممتازة للأهداف المتعددة';
        strongestSkillEn = 'Fluid Target Transitions & Multi-Target Efficiency';
        diagnosticTipAr = 'تنسيق متفوق بين الأهداف. تدرب الآن على تقليل عدد الطلقات الفارغة بين كل هدف وآخر.';
        diagnosticTipEn = 'Superb target coordination. Focus next on eliminating phantom shots during target switches.';
        nextDrillId = 'reaction';
      } else {
        weakestSkillAr = 'تشتت التركيز بين الأهداف المتزامنة وفقدان الترتيب';
        weakestSkillEn = 'Target Priority Confusion & Inefficient Pathing';
        diagnosticTipAr = 'حدد الهدف التالي بنظرك قبل تحريك يدك. الانتقال البصري يجب أن يسبق حركة الماوس بجزء من الثانية.';
        diagnosticTipEn = 'Aquire the next target with your eyes before moving your hand. Visual confirmation should precede hand movement.';
        nextDrillId = 'precision';
      }
      break;

    case 'reaction':
      if (falseStarts > 1) {
        weakestSkillAr = 'استباق الإشارة العصبية (False Starts متكررة)';
        weakestSkillEn = 'Anticipation & Premature Trigger Activation (False Starts)';
        diagnosticTipAr = 'تجنب محاولة تخمين وقت ظهور الإشارة؛ التركيز الساكن يمنحك استجابة عصبية أسرع من التوتر الاستباقي.';
        diagnosticTipEn = 'Never guess stimulus timing. Calm visual vigilance produces faster and more consistent motor responses than anxious anticipation.';
        nextDrillId = 'reaction';
      } else {
        strongestSkillAr = 'انضباط عصبي عالي وسرعة استجابة فورية خالية من الأخطاء';
        strongestSkillEn = 'Superb Impulse Control & Clean Reaction Latency';
        diagnosticTipAr = 'استجابة متسقة وممتازة. خذ استراحة قصيرة مدتها دقيقة للحفاظ على انتعاش التوصيل العصبي العضلي.';
        diagnosticTipEn = 'Consistent, clean reflex execution. Take a 60-second break to keep neuromuscular firing sharp.';
        nextDrillId = 'movement';
      }
      break;

    case 'movement':
      if (accuracy >= 75) {
        strongestSkillAr = 'إتقان استقرار التوقف التام (Counter-Strafing Mastery)';
        strongestSkillEn = 'Counter-Strafing Mastery & Full Momentum Reset';
        diagnosticTipAr = 'توقيت مثالي لإلغاء الحركة قبل الإطلاق. حاول تدريب هذه المهارة على مسافات أبعد.';
        diagnosticTipEn = 'Excellent counter-strafe timing. Challenge yourself at extended target distances.';
        nextDrillId = 'precision';
      } else {
        weakestSkillAr = 'إطلاق النار أثناء استمرار الاندفاع الحركي الجانبي';
        weakestSkillEn = 'Shooting While Retaining Residual Lateral Momentum';
        diagnosticTipAr = 'في ألعاب التصويب التكتيكية مثل CS2 و Valorant، الضغط العكسي الخاطف على مفتاح الاتجاه المعاكس يلغي تشتت الرصاص فوراً.';
        diagnosticTipEn = 'In tactical FPS titles, tap the opposing strafe key to instantly cancel momentum and reset bullet bloom before pulling the trigger.';
        nextDrillId = 'movement';
      }
      break;
  }

  // Fallback defaults if not set
  if (!strongestSkillAr) strongestSkillAr = 'ثبات التصويب وإكمال الجلسة التدريبية بنجاح';
  if (!strongestSkillEn) strongestSkillEn = 'Consistent Aim Stability & Drill Completion';
  if (!weakestSkillAr) weakestSkillAr = 'الحاجة لتكثيف التدريب على الأهداف السريعة';
  if (!weakestSkillEn) weakestSkillEn = 'Room for Faster Acquisition on Dynamic Targets';
  if (!diagnosticTipAr) diagnosticTipAr = 'وازن بين دقة الطلقة الأولى وسرعة اليد، وراجع سجل الجلسات دورياً لرصد التطور الفعلي.';
  if (!diagnosticTipEn) diagnosticTipEn = 'Balance first-shot precision with acquisition speed, and review historical trends regularly.';

  return {
    strongestSkill: isArabic ? strongestSkillAr : strongestSkillEn,
    weakestSkill: isArabic ? weakestSkillAr : weakestSkillEn,
    diagnosticTip: isArabic ? diagnosticTipAr : diagnosticTipEn,
    nextDrillId
  };
}
